/* Product page blocks, set 2 (main-product): tabs, discount code, quantity gifts, urgency, info popup, anchor scroll.
   Custom elements are plain DOM, so they survive the section re-render done on variant change. */
(() => {
  if (window.__ntProductBlocks) return;
  window.__ntProductBlocks = true;
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- tabs on desktop, accordion on phones ---------- */
  class NtTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('.nt-tabs__tab')];
      this.panels = [...this.querySelectorAll('[data-nt-panel]')];
      this.heads = this.panels.map((p) => p.querySelector('.nt-tabs__head'));
      this.bodies = this.panels.map((p) => p.querySelector('.nt-tabs__body'));
      this.mq = window.matchMedia('(min-width: 750px)');
      this.active = 0;
      this.open = new Set(this.dataset.openFirst === 'true' ? [0] : []);
      this.onMq = () => this.render();
      this.mq.addEventListener('change', this.onMq);
      this.tabs.forEach((t, i) => {
        t.addEventListener('click', () => this.select(i));
        t.addEventListener('keydown', (e) => this.keys(e, i));
      });
      this.heads.forEach((h, i) => h.addEventListener('click', () => this.toggle(i)));
      this.render();
    }
    disconnectedCallback() { this.mq && this.mq.removeEventListener('change', this.onMq); }
    accordion() { return this.dataset.mobile === 'accordion' && !this.mq.matches; }
    select(i) { this.active = i; this.render(); }
    toggle(i) { this.open.has(i) ? this.open.delete(i) : this.open.add(i); this.render(); }
    keys(e, i) {
      const n = this.tabs.length;
      let to = null;
      if (e.key === 'ArrowRight') to = (i + 1) % n;
      else if (e.key === 'ArrowLeft') to = (i - 1 + n) % n;
      else if (e.key === 'Home') to = 0;
      else if (e.key === 'End') to = n - 1;
      if (to === null) return;
      e.preventDefault();
      this.select(to);
      this.tabs[to].focus();
    }
    render() {
      const acc = this.accordion();
      this.bodies.forEach((body, i) => {
        const shown = acc ? this.open.has(i) : i === this.active;
        body.hidden = !shown;
        this.heads[i].setAttribute('aria-expanded', String(acc ? shown : i === this.active));
        body.setAttribute('role', acc ? 'region' : 'tabpanel');
      });
      this.tabs.forEach((t, i) => {
        t.setAttribute('aria-selected', String(i === this.active));
        t.tabIndex = i === this.active ? 0 : -1;
      });
    }
  }

  /* ---------- clickable discount code ---------- */
  class NtDiscount extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('.nt-disc__btn');
      this.btn.addEventListener('click', () => this.use());
    }
    async copy(code) {
      try { await navigator.clipboard.writeText(code); return true; } catch (e) {
        try {
          const ta = document.createElement('textarea');
          ta.value = code; ta.style.position = 'fixed'; ta.style.opacity = '0';
          document.body.appendChild(ta); ta.select();
          const ok = document.execCommand('copy'); ta.remove(); return ok;
        } catch (err) { return false; }
      }
    }
    async use() {
      const code = this.dataset.code;
      await this.copy(code);
      if (this.dataset.apply === 'true') {
        try { await fetch(`/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin', redirect: 'manual' }); } catch (e) { /* offline: code stays copied */ }
        document.dispatchEvent(new CustomEvent('nt:discount-saved', { detail: { code } }));
      }
      this.querySelector('.nt-disc__state--idle').hidden = true;
      this.querySelector('.nt-disc__state--done').hidden = false;
    }
  }

  /* ---------- quantity gifts: follows the quantity box ---------- */
  class NtGifts extends HTMLElement {
    connectedCallback() {
      this.cards = [...this.querySelectorAll('.nt-gifts__card')];
      this.hint = this.querySelector('[data-nt-gifts-hint]');
      if (this.dataset.by !== 'quantity') return;
      this.onInput = (e) => {
        if (e.target && e.target.matches && e.target.matches('input[name="quantity"]')) this.update();
      };
      document.addEventListener('change', this.onInput);
      document.addEventListener('input', this.onInput);
      this.update();
    }
    disconnectedCallback() {
      if (!this.onInput) return;
      document.removeEventListener('change', this.onInput);
      document.removeEventListener('input', this.onInput);
    }
    qty() {
      const input = document.querySelector('product-info input[name="quantity"], form[action*="/cart/add"] input[name="quantity"]');
      const n = input ? parseInt(input.value, 10) : 1;
      return Number.isFinite(n) && n > 0 ? n : 1;
    }
    update() {
      const q = this.qty();
      let next = null;
      this.cards.forEach((c) => {
        const min = parseInt(c.dataset.min, 10) || 1;
        const on = q >= min;
        if (on) c.dataset.unlocked = 'true'; else { c.removeAttribute('data-unlocked'); if (next === null) next = min; }
      });
      if (!this.hint) return;
      this.hint.textContent = next === null
        ? this.dataset.done
        : this.dataset.hint.replace('[remaining]', next - q).replace('[min]', next);
    }
  }

  /* ---------- urgency: dispatch cut-off countdown ---------- */
  class NtUrgency extends HTMLElement {
    connectedCallback() {
      this.cutoff = this.dataset.cutoff;
      if (!this.cutoff) return;
      this.target = this.querySelector('[data-nt-urgency-text]');
      this.template = this.target.textContent;
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    tick() {
      const [h, m] = this.cutoff.split(':').map((n) => parseInt(n, 10) || 0);
      const now = new Date();
      const end = new Date(now);
      end.setHours(h, m, 0, 0);
      const weekend = (d) => d.getDay() === 0 || d.getDay() === 6;
      const skip = this.dataset.skipWeekend === 'true';
      if (now >= end || (skip && weekend(now))) {
        if (this.dataset.lateText) this.target.textContent = this.dataset.lateText; else this.hidden = true;
        clearInterval(this.timer);
        return;
      }
      const s = Math.floor((end - now) / 1000);
      const pad = (n) => String(n).padStart(2, '0');
      const t = `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
      this.target.textContent = this.template.replace('[time]', t);
    }
  }

  /* ---------- info popup (native dialog) ---------- */
  class NtPopup extends HTMLElement {
    connectedCallback() {
      this.dlg = this.querySelector('dialog');
      this.opener = this.querySelector('.nt-popup__open');
      this.opener.addEventListener('click', () => {
        if (typeof this.dlg.showModal === 'function') this.dlg.showModal(); else this.dlg.setAttribute('open', '');
      });
      this.dlg.addEventListener('click', (e) => {
        if (e.target === this.dlg || e.target.closest('[data-nt-popup-close]')) this.dlg.close();
      });
    }
  }

  [['nt-tabs', NtTabs], ['nt-discount', NtDiscount], ['nt-gifts', NtGifts], ['nt-urgency', NtUrgency], ['nt-popup', NtPopup]]
    .forEach(([name, cls]) => { if (!customElements.get(name)) customElements.define(name, cls); });

  /* ---------- in-page anchor links from the "view details" block ---------- */
  document.addEventListener('click', (e) => {
    const a = e.target.closest && e.target.closest('a[data-nt-scroll]');
    if (!a) return;
    const target = document.getElementById(a.getAttribute('href').slice(1));
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
    if (target.tagName === 'DETAILS') target.open = true;
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  });
})();
